import type { FilterState } from '../useListing/filter';
import type { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import type { PropertyFilters } from '~/core/entities/property/filters';

import { propertyTypes } from '~/core/entities/CRMEnums';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { slugify } from '~~/utils';
import { computed } from 'vue';

import { mapMacroregionToState } from '~/components/SearchSuggest/mappings';

interface UseCanonicalOptions {
	customUrl?: string;
	customPath?: string;
}

const LISTING_PAGE_ROUTES = [
	'PropertiesListing',
	'PropertiesListingMap'
] as const;

interface AddressLocation {
	locationName: string;
	street: string;
	streetNumber: string;
	neighborhood: string;
	city: string;
	state: string;
}

interface AddressSlugParts {
	visual: AddressLocation;
	route: AddressLocation;
}

const EMPTY_ADDRESS_LOCATION: AddressLocation = {
	locationName: '',
	street: '',
	streetNumber: '',
	neighborhood: '',
	city: '',
	state: ''
};

const EMPTY_ADDRESS_SLUG_PARTS: AddressSlugParts = {
	visual: EMPTY_ADDRESS_LOCATION,
	route: EMPTY_ADDRESS_LOCATION
};

// Pure helper functions
function splitByDelimiter(delimiter: string) {
	return (text: string): string[] =>
		text.split(delimiter).map(part => part.trim());
}

const splitByComma = splitByDelimiter(',');
const splitByHyphen = splitByDelimiter('-');

function extractCityState(
	part: string
): Pick<AddressLocation, 'city' | 'state'> {
	const [city = '', state = ''] = splitByHyphen(part);
	return { city, state };
}

function isPOIFormat(parts: string[]): boolean {
	return parts[1]?.includes('-') ?? false;
}

function isStreetWithNumberFormat(parts: string[]): boolean {
	return parts[1] ? /^\d+$/.test(parts[1].trim()) : false;
}

function parsePOIAddress(parts: string[]): AddressLocation {
	const locationName = parts[0] ?? '';
	const [street = '', neighborhood = ''] = splitByHyphen(parts[1] ?? '');
	const { city, state } = extractCityState(parts[2] ?? '');

	return { locationName, street, streetNumber: '', neighborhood, city, state };
}

function parseStreetWithNumberAddress(parts: string[]): AddressLocation {
	const street = parts[0] ?? '';
	const streetNumber = parts[1] ?? '';
	const neighborhood = parts[2] ?? '';
	const { city, state } = extractCityState(parts[parts.length - 1] ?? '');

	return {
		locationName: street,
		street,
		streetNumber,
		neighborhood,
		city,
		state
	};
}

function parseSimpleStreetAddress(parts: string[]): AddressLocation {
	const street = parts[0] ?? '';
	const neighborhood = parts[1] ?? '';
	const { city, state } = extractCityState(parts[parts.length - 1] ?? '');

	return {
		locationName: street,
		street,
		streetNumber: '',
		neighborhood,
		city,
		state
	};
}

function parseAddressSlug(addressSlug: string): AddressLocation {
	const parts = splitByComma(addressSlug);

	if (isPOIFormat(parts)) {
		return parsePOIAddress(parts);
	}

	if (isStreetWithNumberFormat(parts)) {
		return parseStreetWithNumberAddress(parts);
	}

	return parseSimpleStreetAddress(parts);
}

function toSlugifiedLocation(visual: AddressLocation): AddressLocation {
	return {
		locationName: slugify(visual.locationName),
		street: slugify(visual.street),
		streetNumber: slugify(visual.streetNumber),
		neighborhood: slugify(visual.neighborhood),
		city: slugify(visual.city),
		state: slugify(visual.state)
	};
}

function buildAddressCanonicalPath(
	address: AddressLocation,
	propertyType?: PropertyTypeEnum
): string {
	const { neighborhood, city, state, street, locationName } = address;

	const locationParts = [neighborhood, city, state, 'brasil'].filter(Boolean);
	const locationPath = locationParts.join('-');

	if (!locationPath) {
		return '';
	}

	let canonicalPath = `/venda/imoveis/${locationPath}`;

	// For POI format, locationName differs from street (e.g., "parque-ibirapuera" vs "av-pedro-alvares-cabral")
	const isPOI = locationName && locationName !== street;
	if (isPOI) {
		canonicalPath = `${canonicalPath}/${locationName}`;
	} else if (street) {
		canonicalPath = `${canonicalPath}/${street}`;
	}

	if (propertyType) {
		const translatedPropertyType = propertyTypes.find(
			type => type.identifier === propertyType
		);

		if (translatedPropertyType) {
			canonicalPath = `${canonicalPath}/${slugify(translatedPropertyType.name)}`;
		}
	}

	return canonicalPath;
}

function buildAddressSlugParts(
	addressSlug: string | undefined
): AddressSlugParts {
	if (!addressSlug) {
		return EMPTY_ADDRESS_SLUG_PARTS;
	}

	const visual = parseAddressSlug(addressSlug);
	const route = toSlugifiedLocation(visual);

	return { visual, route };
}

function isListingPage(routeName: string | symbol | null | undefined): boolean {
	if (!routeName || typeof routeName === 'symbol') {
		return false;
	}

	return LISTING_PAGE_ROUTES.some(name => routeName === name);
}

function buildMacroregionCanonicalFilters(
	filters: PropertyFilters
): PropertyFilters {
	const macroregion = filters.macroregions![0]!;
	const state = mapMacroregionToState(macroregion);

	return {
		states: [state],
		macroregions: filters.macroregions,
		...(filters.propertyTypes?.length && {
			propertyTypes: filters.propertyTypes
		})
	};
}

function buildCanonicalFromFilters(
	filters: PropertyFilters,
	addressSlugDetails: AddressSlugParts,
	origin: string,
	currentPath: string
): string {
	// Handle address-based canonical
	if (filters.addressSlug) {
		let propertyType = undefined;

		if (filters.propertyTypes) propertyType = filters.propertyTypes[0];

		const addressPath = buildAddressCanonicalPath(
			addressSlugDetails.route,
			propertyType
		);

		if (addressPath) {
			return `${origin}${addressPath}`;
		}
	}

	if (filters.regions?.length || filters.cities?.length) {
		const canonicalFilters: PropertyFilters = {
			...(filters.states?.length && { states: filters.states }),
			...(filters.cities?.length && { cities: filters.cities }),
			...(filters.regions?.length && { regions: filters.regions }),
			...(filters.propertyTypes?.length && {
				propertyTypes: filters.propertyTypes
			})
		};
		const path = PropertyFiltersPresenter.getPathFromFilters(canonicalFilters);
		return `${origin}${path.split('?')[0]}`;
	}

	if (filters.macroregions?.length) {
		const macroregionFilters = buildMacroregionCanonicalFilters(filters);
		const path =
			PropertyFiltersPresenter.getPathFromFilters(macroregionFilters);
		const basePath = path.split('?')[0];
		const macroregionsQueryParam = `macroregions=${encodeURIComponent(filters.macroregions[0]!)}`;
		return `${origin}${basePath}?${macroregionsQueryParam}`;
	}

	// Handle standard filters canonical (strip query params)
	// For pages with locationSlug in the path, preserve the path without query params
	// This handles cases like /venda/imoveis/sao-paulo-sp-brasil?cities=São+Paulo
	const pathFromFilters = PropertyFiltersPresenter.getPathFromFilters(filters);
	const formattedPath = pathFromFilters.split('?')[0];

	// If formattedPath is just '/' or empty but we have a valid currentPath,
	// use currentPath without query params
	if (
		(!formattedPath || formattedPath === '/') &&
		currentPath &&
		currentPath !== '/'
	) {
		return `${origin}${currentPath.split('?')[0]}`;
	}

	return `${origin}${formattedPath}`;
}

function calculateCanonicalUrl(
	customUrl: string | undefined,
	customPath: string | undefined,
	origin: string,
	routeName: string | symbol | null | undefined,
	routePath: string,
	filters: PropertyFilters | null,
	addressSlugDetails: AddressSlugParts
): string {
	// Priority 1: Custom URL override
	if (customUrl) {
		return customUrl;
	}

	// Priority 2: Custom path override
	if (customPath) {
		return `${origin}${customPath}`;
	}

	// Priority 3: Filters-based canonical (only on listing pages)
	if (isListingPage(routeName) && filters) {
		return buildCanonicalFromFilters(
			filters,
			addressSlugDetails,
			origin,
			routePath
		);
	}

	// Fallback: Current route path
	return `${origin}${routePath}`;
}

export function useCanonical({
	customUrl,
	customPath
}: UseCanonicalOptions = {}) {
	const config = useRuntimeConfig();
	const route = useRoute();
	const ORIGIN = config.public.ORIGIN;

	// We need to use undefined here because the filters state can be empty on the first time this composable is used
	const filtersState = useState<FilterState | undefined>(
		'listing-property-filters'
	);

	const filters = computed<PropertyFilters | null>(
		() => filtersState.value?.filters || null
	);

	const addressSlugDetails = computed<AddressSlugParts>(() =>
		buildAddressSlugParts(filters.value?.addressSlug)
	);

	const canonicalUrl = computed(() => {
		const calculatedCanonical = calculateCanonicalUrl(
			customUrl,
			customPath,
			ORIGIN as string,
			route.name,
			route.path,
			filters.value,
			addressSlugDetails.value
		);

		// According to Google's best practices, canonical tags should always be present,
		// even when pointing to the current page (self-referencing canonical).
		// This helps prevent duplicate content issues and reinforces the preferred URL.
		return calculatedCanonical;
	});

	return {
		canonicalUrl
	};
}
